/**
 * StructureSetDisplayObject3D — Renders a set of mesh structures in 3D.
 *
 * Follows the centerline pattern: one display feature holds all structures,
 * and this display object creates one vtkActor per structure internally.
 *
 * Highlight logic (hovered/selected) is handled entirely within the display
 * object based on properties set by the workflow service.
 */

import vtkActor from '@kitware/vtk.js/Rendering/Core/Actor';
import vtkMapper from '@kitware/vtk.js/Rendering/Core/Mapper';
import type vtkRenderer from '@kitware/vtk.js/Rendering/Core/Renderer';

import { DosimetryStateService } from '../../../workflows/oncology/dosimetry/services/dosimetry-state-service';
import type { RGB } from '../../domain/types';
import { BaseViewport } from '../../viewports';
import type {
    DisplayFeatureType,
    IDisplayFeature,
    IDisplayObject,
    IHoverable,
    PickInfo,
    StructureSetDisplayProperties,
    ViewMode,
} from '../types';
import type { StructureSetFeatureData } from './structure-set-types';

/** Opacity when structure is not selected or hovered */
const DEFAULT_OPACITY = 0.05;
/** Opacity when structure is highlighted (selected or hovered) */
const HIGHLIGHT_OPACITY = 0.8;

/**
 * Flag to quickly select the render mode used
 * edges: Non highlighted segments are rendered using wireframe reprensetation
 * rim: Use a modified shader to render meshes silhouette (Fresnel rim)
 */

type DEFAULT_RENDERING_MODE_TYPE = 'edges' | 'rim';
const DEFAULT_RENDERING_MODE: DEFAULT_RENDERING_MODE_TYPE = 'edges';

export class StructureSetDisplayObject3D implements IDisplayObject, IHoverable {
    readonly feature: IDisplayFeature<DisplayFeatureType.STRUCTURE_SET>;
    readonly viewMode: ViewMode;
    readonly hoverable = true;

    private renderer: vtkRenderer | null = null;
    /** labelValue → vtkActor */
    private actors = new Map<number, vtkActor>();
    /** labelValue → vtkMapper */
    private mappers = new Map<number, vtkMapper>();
    /** Reverse lookup: vtkActor → labelValue (for picking) */
    private actorToLabel = new Map<vtkActor, number>();
    /** Currently hovered actor's label value (from 3D picking, not from properties) */
    private pickedLabel: number | null = null;

    constructor(feature: IDisplayFeature<DisplayFeatureType.STRUCTURE_SET>, viewMode: ViewMode) {
        this.feature = feature;
        this.viewMode = viewMode;
    }

    private get data(): StructureSetFeatureData {
        return this.feature.data as StructureSetFeatureData;
    }

    private get props(): StructureSetDisplayProperties {
        return this.feature.properties;
    }

    initialize(viewport: BaseViewport): void {
        this.renderer = viewport.getRenderer(0);
        this.createActorsFromLoadedMeshes();
    }

    update(): void {
        // Create actors for any newly loaded meshes
        this.createActorsFromLoadedMeshes();
        this.applyProperties();
    }

    dispose(): void {
        for (const actor of this.actors.values()) {
            this.renderer?.removeActor(actor);
            actor.delete();
        }
        for (const mapper of this.mappers.values()) {
            mapper.delete();
        }
        this.actors.clear();
        this.mappers.clear();
        this.actorToLabel.clear();
        this.renderer = null;
    }

    getActors(): vtkActor[] {
        return [...this.actors.values()];
    }

    // ── IHoverable ──

    onHoverStart(pickInfo: PickInfo): boolean {
        if (!pickInfo.actor) return false;
        const label = this.actorToLabel.get(pickInfo.actor);
        if (label === undefined) return false;
        this.pickedLabel = label;
        this.applyProperties();
        this.renderer?.getRenderWindow()?.render();
        return true;
    }

    onHoverEnd(): void {
        this.pickedLabel = null;
        this.applyProperties();
        this.renderer?.getRenderWindow()?.render();
    }

    // ── Private ──

    private createActorsFromLoadedMeshes(): void {
        if (!this.renderer) return;

        for (const [labelValue, polyData] of this.data.meshes) {
            if (this.actors.has(labelValue)) continue;

            const mapper = vtkMapper.newInstance();
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (mapper as any).setInputData(polyData);

            if (DEFAULT_RENDERING_MODE === 'rim') {
                this.addSilhouetteShader(mapper);
            }

            const actor = vtkActor.newInstance();
            actor.setMapper(mapper);

            this.actors.set(labelValue, actor);
            this.mappers.set(labelValue, mapper);
            this.actorToLabel.set(actor, labelValue);
            this.renderer.addActor(actor);
        }

        this.applyProperties();
    }

    /**
     * Injects a Fresnel rim silhouette effect into the fragment shader via VTK.js
     * view-specific shader replacements.
     *
     * The effect boosts fragment alpha at grazing angles (NdotV → 0), making structure
     * edges visible even at the low default opacity (0.1). At full opacity (highlighted),
     * the max() has no impact since the alpha is already 1.0.
     *
     * How the replacement works:
     *   - replaceFirst: true  → PRE-replacement, runs before VTK expands //VTK::Light::Impl
     *   - We keep //VTK::Light::Impl in our replacement so VTK still expands it to lighting code
     *   - Our silhouette lines are appended after the marker, running after the final light pass
     */
    private addSilhouetteShader(mapper: vtkMapper): void {
        // getViewSpecificProperties() returns `object` in VTK.js types; cast to access OpenGL API.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const viewProps = mapper.getViewSpecificProperties() as any;

        viewProps.OpenGL = {
            ShaderReplacements: [
                {
                    shaderType: 'Fragment',
                    originalValue: '//VTK::Light::Impl',
                    // PRE replacement: runs before VTK expands shader hooks.
                    // Keeps the marker so the standard lighting still executes.
                    replaceFirst: true,
                    replacementValue: [
                        '//VTK::Light::Impl',
                        // normalVCVSOutput is already normalized by //VTK::Normal::Impl.
                        // In view coordinates the camera faces -Z, so view direction = (0,0,1).
                        // NdotV → 0 at silhouette edges, → 1 at faces looking toward the camera.
                        'float NdotV = abs(dot(normalVCVSOutput, vec3(0.0, 0.0, 1.0)));',
                        'float silhouetteFactor = smoothstep(0.3, 0.8, 1.0 - NdotV);',
                        'gl_FragData[0].a = max(gl_FragData[0].a, silhouetteFactor * 0.9);',
                    ],
                    replaceAll: false,
                },
            ],
        };
    }

    private applyProperties(): void {
        const { colors, kind, hoveredKey, selectedKeys } = this.props;

        for (const [labelValue, actor] of this.actors) {
            const key = DosimetryStateService.structureKey(kind, labelValue);
            const isHovered = key === hoveredKey || labelValue === this.pickedLabel;
            const isSelected = selectedKeys.has(key);

            const color: RGB = colors.get(labelValue) ?? [0.6, 0.6, 0.6];
            const opacity = isHovered || isSelected ? HIGHLIGHT_OPACITY : DEFAULT_OPACITY;

            const property = actor.getProperty();
            property.setColor(color[0], color[1], color[2]);
            property.setOpacity(opacity);
            property.setAmbient(0.4);
            property.setDiffuse(0.8);
            property.setSpecular(0.1);
            property.setSpecularPower(20);

            // Show edges with slightly darker color when not selected
            if (DEFAULT_RENDERING_MODE === 'edges') {
                property.setEdgeVisibility(!isHovered && !isSelected);
                property.setEdgeColor(color[0] * 0.8, color[1] * 0.8, color[2] * 0.8);
            }
        }
    }
}
